import { useEffect, useState } from "react";
import api, { inr, formatDate, BACKEND_URL } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import StatusPill from "@/components/StatusPill";
import { toast } from "sonner";
import { Check, X, CreditCard, Download } from "lucide-react";

export default function AdminPayouts() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [payDialog, setPayDialog] = useState(null); // payout being marked as paid
  const [utr, setUtr] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/payouts${status ? `?status=${status}` : ""}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (id, decision) => {
    await api.post(`/admin/payouts/${id}/decision?decision=${decision}`);
    toast.success(`Payout ${decision}d`);
    load();
  };

  const markPaid = async () => {
    if (!utr.trim()) { toast.error("UTR / reference number is required"); return; }
    setBusy(true);
    try {
      await api.post(
        `/admin/payouts/${payDialog.id}/decision?decision=paid`,
        { utr: utr.trim(), method, note: note.trim() },
      );
      toast.success(`Payout marked paid — UTR ${utr.trim()}`);
      setPayDialog(null); setUtr(""); setMethod("bank_transfer"); setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadReceipt = async (id) => {
    const token = localStorage.getItem("samara_token");
    const res = await fetch(`${BACKEND_URL}/api/invoices/payout/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `payout-${id.slice(0,8)}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout title="Payout Queue" subtitle={`${rows.length} ${status || "all"}`}>
      <div className="mb-4 flex gap-2">
        {[{ k: "pending", l: "Pending" }, { k: "approved", l: "Approved" }, { k: "paid", l: "Paid" }, { k: "rejected", l: "Rejected" }, { k: "", l: "All" }].map((s) => (
          <button key={s.k} data-testid={`pay-tab-${s.k || "all"}`} onClick={() => setStatus(s.k)} className={`px-3 py-1.5 text-xs border rounded-sm ${status === s.k ? "bg-navy text-gold border-navy" : "border-border text-navy hover:border-gold"}`}>{s.l}</button>
        ))}
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-navy text-white">
            <tr>{["User", "Amount", "TDS", "Net", "UTR / Ref", "Status", "Date", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-navy/50">Nothing to show</td></tr>}
            {rows.map((p) => (
              <tr key={p.id} data-testid={`payout-${p.id}`} className="border-b border-border last:border-0 hover:bg-cream/40">
                <td className="px-4 py-3 font-medium text-navy">{p.user_code}</td>
                <td className="px-4 py-3 text-navy">{inr(p.amount)}</td>
                <td className="px-4 py-3 text-rose">−{inr(p.tds)}</td>
                <td className="px-4 py-3 font-semibold text-olive">{inr(p.net_amount)}</td>
                <td className="px-4 py-3 font-mono text-xs text-navy/70" data-testid={`utr-${p.id}`}>{p.utr || "—"}</td>
                <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                <td className="px-4 py-3 text-xs text-navy/50">{formatDate(p.created_at)}</td>
                <td className="px-4 py-3">
                  {p.status === "pending" && (
                    <div className="flex gap-1">
                      <button data-testid={`approve-${p.id}`} onClick={() => decide(p.id, "approve")} className="p-1.5 bg-olive/10 text-olive rounded-sm"><Check size={14} /></button>
                      <button data-testid={`reject-${p.id}`} onClick={() => decide(p.id, "reject")} className="p-1.5 bg-rose/10 text-rose rounded-sm"><X size={14} /></button>
                    </div>
                  )}
                  {p.status === "approved" && (
                    <button data-testid={`paid-${p.id}`} onClick={() => setPayDialog(p)} className="p-1.5 bg-navy/10 text-navy rounded-sm" title="Mark as paid (UTR required)"><CreditCard size={14} /></button>
                  )}
                  <button data-testid={`pay-receipt-${p.id}`} onClick={() => downloadReceipt(p.id)} className="ml-1 p-1.5 text-navy/60 hover:text-gold" title="Download PDF"><Download size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mark as Paid dialog */}
      {payDialog && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm"
             onClick={() => !busy && setPayDialog(null)} data-testid="mark-paid-modal">
          <div className="w-full max-w-md bg-white rounded-sm border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border">
              <div className="text-[10px] uppercase tracking-widest text-royal">Confirm bank transfer</div>
              <h3 className="font-serif text-lg text-navy">Mark payout as paid</h3>
              <p className="text-[11px] text-navy/50 mt-0.5">
                {payDialog.user_code} · Net {inr(payDialog.net_amount)}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-navy/50">UTR / Reference Number *</span>
                <input type="text" value={utr} onChange={(e) => setUtr(e.target.value)}
                  data-testid="mark-paid-utr" placeholder="e.g. HDFC1234567890"
                  className="w-full mt-1 border border-line rounded-sm px-3 py-2 text-sm font-mono focus:border-royal outline-none" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-navy/50">Method</span>
                <select value={method} onChange={(e) => setMethod(e.target.value)}
                  data-testid="mark-paid-method"
                  className="w-full mt-1 border border-line rounded-sm px-3 py-2 text-sm focus:border-royal outline-none">
                  <option value="bank_transfer">Bank transfer (NEFT/IMPS/RTGS)</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-navy/50">Remarks (optional)</span>
                <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                  data-testid="mark-paid-note"
                  className="w-full mt-1 border border-line rounded-sm px-3 py-2 text-sm focus:border-royal outline-none" />
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => setPayDialog(null)} disabled={busy}
                  className="px-4 py-2 text-xs text-navy/60 hover:text-navy">Cancel</button>
                <button onClick={markPaid} disabled={busy || !utr.trim()}
                  data-testid="mark-paid-confirm"
                  className="px-4 py-2 text-xs bg-royal text-white hover:bg-royal-deep rounded-sm disabled:opacity-50">
                  {busy ? "Saving…" : "Confirm & mark paid"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
